import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { headersData3 } from "../data/tableData";
import Dropdown from "../components/Dropdown";
import OutletOrderHistoryTable2 from "../components/OutletOrderHistoryTable2";
import useDataFetch from "../hooks/useDataFetch";
import { MobileResponsive } from "./MobileResponsive";
import { useDispatch } from "react-redux";
import {
  cancelTripService,
  updateCancelTripData,
} from "../features/outletSlice";
import toast from "react-hot-toast";
import PrimaryButton from "../components/PrimaryButton";
import DynamicMetaTags from "../components/DynamicMetaTags";
import Avatar from "react-avatar";
import { fetchOrdersApi } from "../api/services/orderService";

const PAGE_SIZE = 15;

const timeToParam = (label) => {
  switch (label) {
    case "Last 3 days": return 3;
    case "Last 7 days": return 7;
    case "Last 30 days": return 30;
    case "Last 90 days": return 90;
    default: return "all";
  }
};

const OutletsHistory = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { data, isLoading, mutate } = useDataFetch(
    `/admin/outlet-detail/${id}`
  );

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTime, setSelectedTime] = useState("All time");
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");

  const sidebarRefs = {
    cancelTrip: useRef(null),
  };

  const [sidebars, setSidebars] = useState({
    cancelTrip: false,
  });

  const [cancelTripData, setCancelTripData] = useState({});
  const [cancelTrip, setCancelTrip] = useState(false);

  const timeOptions = [
    "Last 3 days",
    "Last 7 days",
    "Last 30 days",
    "Last 90 days",
    "All time",
  ];

  // Fetch orders from new API
  useEffect(() => {
    setOrdersLoading(true);
    fetchOrdersApi({
      outletId: id,
      page: currentPage,
      time: timeToParam(selectedTime),
      orderId: activeSearch || undefined,
    })
      .then((res) => {
        setOrders(res.orders || []);
        setTotalCount(res.total || 0);
      })
      .catch(() => {
        setOrders([]);
        setTotalCount(0);
      })
      .finally(() => setOrdersLoading(false));
  }, [id, currentPage, selectedTime, activeSearch]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTime, activeSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !Object.keys(sidebarRefs).some((key) =>
          sidebarRefs[key]?.current?.contains(event.target)
        )
      ) {
        handleCloseSidebar();
      }
    };
    if (Object.values(sidebars).some((val) => val)) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [sidebars]);

  const toggleSidebar = (key, value) => {
    setSidebars((prev) => ({ ...prev, [key]: value }));
  };

  const handleCloseSidebar = () => {
    setSidebars({ cancelTrip: false });
  };

  const handleCancelTrip = (orderData) => {
    setCancelTripData(orderData);
    dispatch(updateCancelTripData(orderData));
    toggleSidebar("cancelTrip", true);
  };

  const handleCancelTripConfirm = async () => {
    const resultAction = await dispatch(
      cancelTripService({
        orderId: cancelTripData?._id,
        driverId: cancelTripData?.driver_id,
      })
    );
    if (cancelTripService.fulfilled.match(resultAction)) {
      toast.success(resultAction?.payload?.message);
      mutate();
      setCancelTrip(true);
      // Refresh orders list
      fetchOrdersApi({
        outletId: id,
        page: currentPage,
        time: timeToParam(selectedTime),
        orderId: activeSearch || undefined,
      }).then((res) => {
        setOrders(res.orders || []);
        setTotalCount(res.total || 0);
      });
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setActiveSearch(searchInput.trim());
  };

  if (isLoading) {
    return (
      <>
        <MobileResponsive />
        <div className="lg:flex hidden items-start">
          <Sidebar />
          <div className="ml-[14.5vw] w-full h-screen flex items-center justify-center bg-gray-50">
            <div className="flex flex-col items-center gap-2">
              <div className="h-12 w-12 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
              <p className="text-gray-600 font-medium">Loading..</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Comprehensive Order History Overview"
        pageDescription="Take control of your outlets with Beep! Effortlessly manage new outlet requests, view detailed outlet information, track payment history, and set pickup prices. Enable or disable outlets with a click!"
        pageKeywords="Beep, outlet management, new outlet requests, outlet details, payment history, set pickup prices, enable outlets, disable outlets, active outlets"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start">
        <Sidebar />

        {Object.values(sidebars).some((isOpen) => isOpen) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-30 transition-opacity duration-500 ease-in-out"></div>
        )}

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary">
          <div className="flex justify-between w-full">
            <Link to={`/outlets/${id}`} className="flex items-center gap-3">
              <div className="bg-[#F75E00] w-5 h-5 flex items-center justify-center rounded-full pr-[1.5px]">
                <img
                  src="/assets/left-white-arrow.svg"
                  alt=""
                  className="w-[6px]"
                />
              </div>
              <span className="font-semibold text-base">Order history</span>
            </Link>

            <div className="flex items-center">
              <form
                className="flex items-center gap-2 rounded-full px-3 border-2 border-[#F7631D] w-[27vw]"
                onSubmit={handleSearchSubmit}
              >
                <div className="relative w-full">
                  <input
                    type="text"
                    placeholder="Search by order ID"
                    className="peer rounded-full py-1.5 text-quaternary placeholder:text-quaternary/60 text-base font-medium w-full outline-none px-1"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
                <button type="submit">
                  <img src="/assets/search.svg" alt="Search" />
                </button>
              </form>
            </div>

            <div className="flex items-center gap-3">
              <Dropdown
                options={timeOptions}
                selected={selectedTime}
                onSelect={setSelectedTime}
                label="Select time"
              />
            </div>
          </div>

          <div className="w-full rounded-lg flex flex-col items-center justify-between gap-4 py-8">
            {ordersLoading ? (
              <div className="flex items-center justify-center w-full py-20">
                <div className="h-10 w-10 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
              </div>
            ) : (
              <div className="flex flex-col gap-5 w-full">
                <OutletOrderHistoryTable2
                  headers={headersData3}
                  data={orders}
                  currentPage={currentPage}
                  totalCount={totalCount}
                  pageSize={PAGE_SIZE}
                  onPageChange={setCurrentPage}
                  handleCancelTrip={handleCancelTrip}
                />
              </div>
            )}
          </div>

          {/* Cancel Trip Sidebar */}
          <div
            ref={sidebarRefs.cancelTrip}
            className={`fixed right-0 top-0 h-full w-[40%] bg-white shadow-lg overflow-y-auto transform transition-transform duration-[500ms] ease-in-out z-40 border-t-4 border-primary ${
              sidebars.cancelTrip ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="p-8 relative flex flex-col items-center">
              <button
                onClick={handleCloseSidebar}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 scale-150"
              >
                &times;
              </button>
              <div className="flex w-[90%] pb-10">
                <div className="flex items-center gap-3 cursor-pointer">
                  <div className="bg-[#F75E00] w-6 h-6 flex items-center justify-center rounded-full">
                    <img
                      src="/assets/left-white-arrow.svg"
                      alt=""
                      className="w-[7px]"
                    />
                  </div>
                  <span className="font-semibold text-[25px]">Cancel trip</span>
                </div>
              </div>
              <div className="border-2 border-quaternary w-[90%] mb-5 px-5 py-1.5 rounded-lg flex items-center gap-3 bg-[#F7F9F8]">
                <Avatar
                  name={data?.outlet?.restaurant_name}
                  size="84"
                  round={true}
                  maxInitials={2}
                  textSizeRatio={2.5}
                  style={{ letterSpacing: "1px" }}
                />
                <div className="text-center">
                  <h1 className="text-xl font-bold tracking-wide capitalize">
                    {data?.outlet?.restaurant_name}
                  </h1>
                </div>
              </div>
              <div className="w-[90%] mb-5 px-5 py-3 rounded-lg flex justify-between items-center shadow-lg">
                <p className="text-[18px] font-semibold w-full">
                  {data?.outlet?.phone_no}
                </p>
                <div className="w-full flex justify-end">
                  <PrimaryButton
                    text={"Call outlet"}
                    path={`tel:${data?.outlet?.phone_no}`}
                    width="w-[65%]"
                    uppercase={false}
                  />
                </div>
              </div>
              <div className="border-2 border-quaternary w-[90%] mb-5 p-[4px] rounded-lg flex flex-col items-center bg-[#F7F9F8]">
                <h1 className="bg-primary w-full text-sm py-[7px] font-bold tracking-wide rounded-md text-center text-white uppercase">
                  Order details
                </h1>
                <div className="w-[94.5%] px-5 py-4 rounded-lg flex flex-col items-center justify-between gap-4 bg-[#F7F9F8]">
                  {[
                    { label: "Order ID", value: cancelTripData?.orderId },
                    { label: "Customer's name", value: cancelTripData?.customer_name },
                    { label: "Driver", value: cancelTripData?.driver },
                    {
                      label: "EE share",
                      value: cancelTripData?.ee_share
                        ? `£${cancelTripData.ee_share}`
                        : "£0",
                    },
                    {
                      label: "Delivery charge",
                      value: cancelTripData?.delivery_charge
                        ? `£${cancelTripData.delivery_charge}`
                        : "—",
                    },
                  ].map((detail, index) => (
                    <div className="flex items-start w-full" key={index}>
                      <p className="2xl:text-[15px] xl:text-[13px] lg:text-[13px] font-semibold tracking-wide 2xl:w-[65%] xl:w-[55%] lg:w-[55%]">
                        {detail.label}
                      </p>
                      <p
                        className={`2xl:text-[15px] xl:text-[13px] lg:text-[13px] font-semibold tracking-wide text-end w-full ${
                          detail.label === "Customer's name" && "capitalize"
                        }`}
                      >
                        {detail.value || "—"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="w-[90%] m-auto flex flex-col justify-center gap-5 py-5">
                {cancelTrip ? (
                  <PrimaryButton
                    text={"Allocate new driver"}
                    width="w-full"
                    uppercase={false}
                    path={`/allocate-new-driver/${cancelTripData?._id}`}
                  />
                ) : (
                  <PrimaryButton
                    text={"Cancel trip"}
                    width="w-full"
                    uppercase={false}
                    event={handleCancelTripConfirm}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OutletsHistory;
